import { useMemo } from 'react';
import { useHistory } from 'react-router';
import { Chatbot, ChatbotChatHistory, CrisisState, CrisisType, useChatbots } from '../../hooks/useChatBot';
import { convertObjectToParams } from '../../components/common/create-params';
import { ROUTE_PATH } from '../../routes/paths';

interface ChatbotSummaryTableProps {
  chatbotMessages: ChatbotChatHistory[];
  onViewChat: (chatId: number) => void;
}

interface SummaryRow {
  chatId: number;
  displayName: string;
  memberLabel: string;
  userId?: number;
  callerRoleId?: number | null;
  summary?: string;
  createdAt?: Date | string;
  crisis_state: CrisisState;
  crisis_type: CrisisType;
}

const ChatbotSummaryTable: React.FC<ChatbotSummaryTableProps> = ({ chatbotMessages, onViewChat }) => {
  const history = useHistory();
  const { data: allChatbots } = useChatbots();

  const summaryRows = useMemo<SummaryRow[]>(() => {
    if (!chatbotMessages?.length) {
      return [];
    }

    const chatbots = allChatbots?.chatbots ?? [];

    return chatbotMessages.map((chat) => ({
      chatId: chat.id,
      displayName: chatbots.find((chatbot: Chatbot) => chatbot.id === chat.chatbot_id)?.display_name ?? '',
      memberLabel: chat.user_display_name?.trim() || (chat.user_id != null ? `User ${chat.user_id}` : '—'),
      userId: chat.user_id,
      callerRoleId: chat.caller_role_id,
      summary: chat.summary ?? '',
      createdAt: chat.created_at,
      crisis_state: chat.crisis_state,
      crisis_type: chat.crisis_type,
    }));
  }, [allChatbots?.chatbots, chatbotMessages]);

  const openMember = (row: SummaryRow) => {
    if (row.userId == null || row.callerRoleId == null) return;
    history.push(
      ROUTE_PATH.MEMBERS_PROFILE +
        convertObjectToParams({
          name: row.memberLabel,
          id: row.userId,
          callerRoleId: row.callerRoleId,
        }),
    );
  };

  return (
    <div className="max-w-7xl overflow-x-auto p-4">
      {summaryRows.length === 0 && (
        <p className="text-sm text-gray-500">No summaries available for the selected filters.</p>
      )}

      {summaryRows.length > 0 && (
        <table className="min-w-full table-fixed border-collapse">
          <caption className="mb-4 text-left">Peersona Chat Summaries</caption>
          <thead>
            <tr className="bg-gray-100">
              <th scope="col" className="border p-2">
                Display Name
              </th>
              <th scope="col" className="w-32 border p-2">
                Chat ID
              </th>
              <th scope="col" className="w-48 border p-2">
                Member
              </th>
              <th scope="col" className="border p-2">
                Summary
              </th>
              <th scope="col" className="w-32 border p-2">
                Crisis State
              </th>
              <th scope="col" className="w-40 border p-2">
                Crisis Type
              </th>
              <th scope="col" className="w-48 border p-2">
                Created At
              </th>
              <th scope="col" className="w-40 border p-2">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {summaryRows.map((row) => {
              const createdAt = row.createdAt ? new Date(row.createdAt) : null;
              const formattedCreatedAt =
                createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt.toLocaleString() : `${row.createdAt ?? ''}`;
              const canOpenMember = row.userId != null && row.callerRoleId != null;

              return (
                <tr key={row.chatId}>
                  <td className="border p-2 font-bold text-gray-700">{row.displayName}</td>
                  <td className="border p-2 font-bold text-gray-700">{row.chatId}</td>
                  <td className="border p-2 font-bold text-gray-700">
                    {canOpenMember ? (
                      <button
                        type="button"
                        className="text-left text-blue-700 underline hover:text-blue-900"
                        onClick={() => openMember(row)}
                      >
                        {row.memberLabel}
                        <span className="ml-1 font-normal text-gray-500">#{row.userId}</span>
                      </button>
                    ) : (
                      row.memberLabel
                    )}
                  </td>
                  <td className="border p-2 text-gray-700">
                    {row.summary ? (
                      <span className="whitespace-pre-line">{row.summary}</span>
                    ) : (
                      <span className="italic text-gray-500">No summary available</span>
                    )}
                  </td>
                  <td className="border p-2 font-bold text-gray-700">{row.crisis_state}</td>
                  <td className="border p-2 font-bold text-gray-700">{row.crisis_type}</td>
                  <td className="border p-2 font-bold text-gray-700">{formattedCreatedAt}</td>
                  <td className="border p-2 text-gray-700">
                    <button
                      type="button"
                      onClick={() => onViewChat(row.chatId)}
                      className="border-primary-600 text-primary-600 hover:bg-primary-50 rounded border bg-white px-3 py-1 text-sm font-medium shadow"
                    >
                      View Chat
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ChatbotSummaryTable;
